/**
 * Mobile-app routing utilities.
 *
 * Goal: when the React app is loaded INSIDE the Capacitor WebView (Android/iOS),
 * users must NEVER see the marketing landing page — they downloaded the app
 * to USE the platform. We force them straight into login/register/role-home.
 *
 * Detection runs SYNCHRONOUSLY (no async, no dynamic import) so we can use it
 * as a route guard that prevents `<PublicHome />` from ever mounting on native.
 *
 * Capacitor injects `window.Capacitor` into the WebView bridge BEFORE the JS
 * bundle executes, so `window.Capacitor?.isNativePlatform?.()` is reliable at
 * the very first render.
 */
import { Navigate } from "react-router-dom";

/**
 * Synchronous native-platform check. Returns true ONLY when running inside
 * the Capacitor (or legacy Cordova) WebView shell — never inside a browser,
 * even on mobile. Safe to call during render.
 */
export function isNativeApp() {
  if (typeof window === "undefined") return false;
  // Primary: Capacitor's own runtime flag (set by the native bridge).
  if (window.Capacitor?.isNativePlatform?.() === true) return true;
  // Backup: Capacitor's custom URL scheme used to load assets on native.
  if (typeof window.location !== "undefined" && /^(capacitor|ionic):$/.test(window.location.protocol)) return true;
  // Last-ditch: legacy Cordova flag (in case someone wraps with Cordova).
  if (window.cordova) return true;
  return false;
}

/**
 * Synchronous role-aware "where should this user land?" resolver.
 * Reads the token off localStorage WITHOUT a network round-trip; we'll let the
 * normal `/login` or role pages handle expired-token cleanup downstream.
 */
function nativeLandingRoute() {
  // Already logged in → straight to /login which hydrates the session and
  // bounces to the role's home. First-time native users → /welcome onboarding
  // carousel. Once they tap Sign Up / Sign In there, they leave /welcome via
  // `replace`, so back-button never traps them on the onboarding screen.
  try {
    const hasToken = !!localStorage.getItem("pollpak_token");
    return hasToken ? "/login" : "/welcome";
  } catch {
    return "/welcome";
  }
}

/**
 * Route-guard component. Wrap a route's `element` with this and on native
 * platforms it synchronously `<Navigate>`s away — `<PublicHome/>` never mounts.
 * On the web it renders `children` unchanged.
 *
 *   <Route path="/" element={<MobileGate><PublicHome /></MobileGate>} />
 */
export function MobileGate({ children }) {
  if (isNativeApp()) {
    return <Navigate to={nativeLandingRoute()} replace />;
  }
  return children;
}

/**
 * Legacy hook export kept for backwards-compat with existing imports inside
 * PublicHome. It's now a no-op since MobileGate handles the redirect before
 * PublicHome ever renders. Safe to remove later.
 */
export default function useMobileAppRedirect() {
  // no-op — MobileGate at the route level supersedes this.
}
